// src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { StagewiseToolbar } from '@stagewise/toolbar-react';
import { useTranslation } from 'react-i18next';
import './i18n';  // Import i18n configuration
import './index.css';  // Make sure this is the only CSS import
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Breadcrumb from './components/Breadcrumb';
import EmailUpdatePopup from './components/EmailUpdatePopup';
import { AuthProvider } from './context/AuthContext';
import { emitFeatureFlagsToDOM } from './utils/featureFlags';
import { normalizeCurrentUrl, extractLanguageFromUrl } from './utils/urlNormalization';
import { initPerformanceOptimizations } from './utils/performanceUtils';
import LoadingSpinner from './components/LoadingSpinner';
import GlobalBigDayPromo from './pages/GlobalBigDayPromo';
import TabTitleEffect from './components/TabTitleEffect';

// Lazy-loaded Pages
const Home = lazy(() => import('./pages/Home'));
const Acomodacoes = lazy(() => import('./pages/Acomodacoes'));
const Lodge = lazy(() => import('./pages/Lodge'));
const Gastronomia = lazy(() => import('./pages/Gastronomia'));
const Birdwatching = lazy(() => import('./pages/Birdwatching'));
const Primatas = lazy(() => import('./pages/Primatas'));
const SafariBoat = lazy(() => import('./pages/SafariBoat'));
const GuiasCampo = lazy(() => import('./pages/GuiasCampo'));
const EstacaoPesquisa = lazy(() => import('./pages/EstacaoPesquisa'));
const NascenteNatural = lazy(() => import('./pages/NascenteNatural'));
const FAQ = lazy(() => import('./pages/FAQ'));
const ComoChegar = lazy(() => import('./pages/ComoChegar'));
const Trilhas = lazy(() => import('./pages/Trilhas'));
const NossaHistoria = lazy(() => import('./pages/NossaHistoria'));
const FalaramDeNos = lazy(() => import('./pages/FalaramDeNos'));
const EventosNatureza = lazy(() => import('./pages/EventosNatureza'));
const Galeria = lazy(() => import('./pages/Galeria'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const InteractiveMenu = lazy(() => import('./pages/InteractiveMenu'));
const Login = lazy(() => import('./pages/Login'));
const NotFound = lazy(() => import('./pages/404'));
const COP30Retreat = lazy(() => import('./pages/COP30Retreat'));
const PhotoUpload = lazy(() => import('./pages/PhotoUpload'));

// Direct Imports (if any specific page should NOT be lazy-loaded - unlikely here)
// import NotFound from './pages/404'; // Example if NotFound should load instantly

// Stagewise configuration
const stagewiseConfig = {
  plugins: []
};

// Component to handle conditional rendering of Navbar and Footer
function AppContent() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const menuPaths = ['/menu', '/en/menu', '/pt/menu'];
  const showHeaderFooter = !menuPaths.includes(location.pathname);

  // Handle URL normalization and language detection
  useEffect(() => {
    const langInfo = extractLanguageFromUrl();

    // Only change language if the URL explicitly specifies it (path or query)
    if (langInfo.format === 'path' || langInfo.format === 'query') {
      if (i18n.language !== langInfo.language) {
        i18n.changeLanguage(langInfo.language);
      }
    }
    // Normalize URL if needed (this is safe to keep)
    normalizeCurrentUrl();
  }, [i18n]);

  return (
    <div className="flex flex-col min-h-screen">
      <TabTitleEffect />
      {showHeaderFooter && <Navbar />}
      <Breadcrumb />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/en/" element={<Home />} />
          <Route path="/pt/" element={<Home />} />
          <Route path="/acomodacoes" element={<Acomodacoes />} />
          <Route path="/en/acomodacoes" element={<Acomodacoes />} />
          <Route path="/pt/acomodacoes" element={<Acomodacoes />} />
          <Route path="/lodge" element={<Lodge />} />
          <Route path="/en/lodge" element={<Lodge />} />
          <Route path="/pt/lodge" element={<Lodge />} />
          <Route path="/gastronomia" element={<Gastronomia />} />
          <Route path="/en/gastronomia" element={<Gastronomia />} />
          <Route path="/pt/gastronomia" element={<Gastronomia />} />
          <Route path="/birdwatching" element={<Birdwatching />} />
          <Route path="/en/birdwatching" element={<Birdwatching />} />
          <Route path="/pt/birdwatching" element={<Birdwatching />} />
          <Route path="/primatas" element={<Primatas />} />
          <Route path="/en/primatas" element={<Primatas />} />
          <Route path="/pt/primatas" element={<Primatas />} />
          <Route path="/safari-boat" element={<SafariBoat />} />
          <Route path="/en/safari-boat" element={<SafariBoat />} />
          <Route path="/pt/safari-boat" element={<SafariBoat />} />
          <Route path="/guias-campo" element={<GuiasCampo />} />
          <Route path="/en/guias-campo" element={<GuiasCampo />} />
          <Route path="/pt/guias-campo" element={<GuiasCampo />} />
          <Route path="/estacao-pesquisa" element={<EstacaoPesquisa />} />
          <Route path="/en/estacao-pesquisa" element={<EstacaoPesquisa />} />
          <Route path="/pt/estacao-pesquisa" element={<EstacaoPesquisa />} />
          <Route path="/nascente-natural" element={<NascenteNatural />} />
          <Route path="/en/nascente-natural" element={<NascenteNatural />} />
          <Route path="/pt/nascente-natural" element={<NascenteNatural />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/en/faq" element={<FAQ />} />
          <Route path="/pt/faq" element={<FAQ />} />
          <Route path="/como-chegar" element={<ComoChegar />} />
          <Route path="/en/como-chegar" element={<ComoChegar />} />
          <Route path="/pt/como-chegar" element={<ComoChegar />} />
          <Route path="/trilhas" element={<Trilhas />} />
          <Route path="/en/trilhas" element={<Trilhas />} />
          <Route path="/pt/trilhas" element={<Trilhas />} />
          <Route path="/nossa-historia" element={<NossaHistoria />} />
          <Route path="/en/nossa-historia" element={<NossaHistoria />} />
          <Route path="/pt/nossa-historia" element={<NossaHistoria />} />
          <Route path="/falaram-de-nos" element={<FalaramDeNos />} />
          <Route path="/en/falaram-de-nos" element={<FalaramDeNos />} />
          <Route path="/pt/falaram-de-nos" element={<FalaramDeNos />} />
          <Route path="/login" element={<Login />} />
          <Route path="/en/login" element={<Login />} />
          <Route path="/pt/login" element={<Login />} />
          <Route path="/eventos-natureza" element={<EventosNatureza />} />
          <Route path="/en/eventos-natureza" element={<EventosNatureza />} />
          <Route path="/pt/eventos-natureza" element={<EventosNatureza />} />
          <Route path="/galeria" element={<Galeria />} />
          <Route path="/en/galeria" element={<Galeria />} />
          <Route path="/pt/galeria" element={<Galeria />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/en/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/pt/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/menu" element={<InteractiveMenu />} />
          <Route path="/en/menu" element={<InteractiveMenu />} />
          <Route path="/pt/menu" element={<InteractiveMenu />} />
          <Route path="/global-big-day" element={<GlobalBigDayPromo />} />
          <Route path="/en/global-big-day" element={<GlobalBigDayPromo />} />
          <Route path="/pt/global-big-day" element={<GlobalBigDayPromo />} />
          <Route path="/cop30-retreat" element={<COP30Retreat />} />
          <Route path="/en/cop30-retreat" element={<COP30Retreat />} />
          <Route path="/pt/cop30-retreat" element={<COP30Retreat />} />
          <Route path="/photo-upload" element={<PhotoUpload />} />
          <Route path="/en/photo-upload" element={<PhotoUpload />} />
          <Route path="/pt/photo-upload" element={<PhotoUpload />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {showHeaderFooter && <Footer />}
      <EmailUpdatePopup />
      {process.env.NODE_ENV === 'production' && (
        <>
          <Analytics />
          <SpeedInsights />
        </>
      )}
    </div>
  );
}

function App() {
  // Initialize performance optimizations and feature flags
  useEffect(() => {
    // Initialize Core Web Vitals optimizations
    initPerformanceOptimizations();
    
    if (process.env.NODE_ENV === 'production') {
      // This emits the feature flags to the DOM to be picked up by Vercel Analytics
      emitFeatureFlagsToDOM();
    }
  }, []);

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <AppContent />
        {process.env.NODE_ENV === 'development' && (
          <StagewiseToolbar config={stagewiseConfig} />
        )}
      </Router>
    </AuthProvider>
  );
}

export default App;